import React, { FC, useEffect, useRef, useState } from 'react';
import { Rect, Text } from 'react-konva';
import type Konva from 'konva';

import { useVisualSelector } from '../../../reduxHooks';
import { selectors } from '../../../ducks/diffUi';
import { getRectSize } from '../../../global/utils';

interface DiffColorType {
  bg: string,
  hoverBg: string,
  textBg: string,
}

export enum DiffRectType {
  CHANGED,
  IGNORED
}

const ignoredRegionBackground = "data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 8.00003L-1.74846e-07 4.00003L4 4.00003L4 8.00003L0 8.00003Z' fill='%23556F83' fill-opacity='0.35'/%3E%3Cpath d='M4 4.00003L4 3.03427e-05L8 3.01679e-05L8 4.00003L4 4.00003Z' fill='%23556F83' fill-opacity='0.35'/%3E%3Cpath d='M8 8L4 8L4 4L8 4L8 8Z' fill='%2332424F' fill-opacity='0.45'/%3E%3Cpath d='M4 4L-3.49691e-07 4L-6.99382e-07 6.99382e-07L4 3.49691e-07L4 4Z' fill='%2332424F' fill-opacity='0.45'/%3E%3C/svg%3E%0A";

export const DIFF_COLORS: Record<DiffRectType, DiffColorType> = {
  [DiffRectType.CHANGED]: {
    textBg: '#FA0EFF',
    bg: '#FA0EFF40',
    hoverBg: '#FA0EFF1A',
  },
  [DiffRectType.IGNORED]: {
    textBg: '#2A5372',
    bg: '#556F8359',
    hoverBg: '#2A53721A',
  },
};

const STROKE_WIDTH = 1;
const FONT_SIZE = 12;
const BASE_PADDING = 4;

export const DiffRect: FC<DiffRectProps> = (props) => {
  const { rect, type, localZoom, setIsHoveringShape } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [textDims, setTextDims] = useState({
    width: 0,
    height: 0,
  });
  const {
    zoom: syncedZoom,
    syncPan,
    diffSettings: {
      emphasizeDiffs,
      hideDiffs,
    },
  } = useVisualSelector(selectors.getDiffUi);
  const color = DIFF_COLORS[type];
  const text = type === DiffRectType.CHANGED ? 'CHANGED' : 'IGNORED';
  const textRef = useRef<Konva.Text>(null);
  const rectRef = useRef<Konva.Rect>(null);
  const zoom = syncPan ? syncedZoom : localZoom;

  useEffect(() => {
    if (textRef.current) {
      setTextDims(textRef.current?.measureSize(text));
    }
  }, [text, zoom]);

  const [image, setImage] = useState<HTMLImageElement | undefined>(undefined);
  useEffect(() => {
    const img = new Image();
    img.onload = function (this: HTMLImageElement) {
      setImage(this);
    };
    img.src = ignoredRegionBackground;
  }, []);

  const PADDING = BASE_PADDING / zoom;

  const sharedProps = {
    height: textDims.height + PADDING * 2,
    width: textDims.width + PADDING * 2,
    x: rect.x,
    y: rect.y,
    opacity: isHovered ? 1 : 0,
  };

  const isTransparentFill = isHovered || (emphasizeDiffs && hideDiffs);
  let fillColor: string | undefined = color.bg;
  let fillPatternImage: HTMLImageElement | undefined;

  if (isTransparentFill || emphasizeDiffs || hideDiffs || type !== DiffRectType.IGNORED) {
    fillPatternImage = undefined;
  } else if (zoom >= 1) {
    fillPatternImage = image;
  }

  if (isTransparentFill) {
    fillColor = 'white';
  } else if (emphasizeDiffs) {
    fillColor = color.textBg;
  } else if (hideDiffs || fillPatternImage) {
    fillColor = undefined;
  }

  const enabledHovered = () => !hideDiffs && setIsHovered(true);
  const disableHovered = () => !hideDiffs && setIsHovered(false);

  useEffect(() => {
    setIsHoveringShape(isHovered);
  }, [isHovered, setIsHoveringShape]);

  const rectSizeProps = getRectSize(rect, zoom);

  return (
    <>
      {/* Stroke - separate element so we can use a different composition type so it's always
      visible */}
      <Rect
        fill="transparent"
        globalCompositeOperation="source-over"
        stroke={isHovered ? color.textBg : undefined}
        strokeWidth={STROKE_WIDTH}
        {...rectSizeProps}
      />
      {/* Actual fill of the rect. Separate layer so we can subtract using destination out when
       using an overlay */}
      <Rect
        fill={fillColor}
        fillPatternImage={fillPatternImage}
        globalCompositeOperation={isTransparentFill ? 'destination-out' : undefined}
        onMouseEnter={enabledHovered}
        onMouseLeave={disableHovered}
        {...rectSizeProps}
      />
      {/* Label background, and label text, respectively. */}
      <Rect
        ref={rectRef}
        cornerRadius={2 / zoom}
        fill={color.textBg}
        fillPatternRepeat="repeat"
        offsetY={textDims.height + PADDING * 2 + STROKE_WIDTH + (
          (rectSizeProps.height - rect.height) / 2
        )}
        stroke={isHovered ? color.textBg : undefined}
        strokeWidth={1}
        {...sharedProps}
      />
      <Text
        ref={textRef}
        fill="white"
        fontSize={FONT_SIZE / zoom}
        offsetX={-PADDING}
        offsetY={textDims.height + PADDING + STROKE_WIDTH + (
          (rectSizeProps.height - rect.height) / 2
        )}
        text={text}
        {...sharedProps}
      />
    </>
  );
};

export interface DiffRectProps {
  rect: { width: number, height: number, x: number, y: number };
  type: DiffRectType;
  localZoom: number;
  setIsHoveringShape: (isHovered: boolean) => void;
}
